import { usePage } from "@inertiajs/react"
import type { PageProps } from "@/types/inertia"

// SparkDrop is single-operator and lives on the U.S. east coast. Users without
// an explicit timezone fall back here so every clock-display stays sane.
export const DEFAULT_TIMEZONE = "America/New_York"

// Returns the current user's timezone (or the default if unset / unauthenticated).
export function useUserTimezone(): string {
  const { props } = usePage<PageProps>()
  return props.current_user?.timezone || DEFAULT_TIMEZONE
}

// Returns a memoizable formatter bound to the current user's timezone.
// Usage:
//   const fmt = useFormatDateTime()
//   fmt(piece.publish_at)           // → "May 23, 6:00 AM"
//   fmt(piece.created_at, { date: false })  // → "6:00 AM"
//   fmt(piece.created_at, { time: false })  // → "May 23"
export function useFormatDateTime() {
  const tz = useUserTimezone()
  return (iso: string | null | undefined, opts: FormatOptions = {}) =>
    formatInZone(iso, tz, opts)
}

type FormatOptions = {
  // Hide one half of the output. Both default to true.
  date?: boolean
  time?: boolean
  // Override defaults — e.g. include the year, drop the month, etc.
  year?: Intl.DateTimeFormatOptions["year"]
  month?: Intl.DateTimeFormatOptions["month"]
  day?: Intl.DateTimeFormatOptions["day"]
}

export function formatInZone(
  iso: string | null | undefined,
  timeZone: string,
  opts: FormatOptions = {},
): string {
  if (!iso) return ""

  const { date = true, time = true, year, month = "short", day = "numeric" } = opts

  const dtfOpts: Intl.DateTimeFormatOptions = { timeZone }
  if (date) {
    if (year) dtfOpts.year = year
    dtfOpts.month = month
    dtfOpts.day = day
  }
  if (time) {
    dtfOpts.hour = "numeric"
    dtfOpts.minute = "2-digit"
  }

  return new Date(iso).toLocaleString(undefined, dtfOpts)
}
